import { gql } from "@apollo/client";
import {
  PLAYBACK_SESSION_ERROR_CODES,
  PLAYBACK_SESSION_ERROR_MESSAGES,
  PLAYBACK_ERROR_MESSAGES,
} from "@/lib/errors";

export const typeDef = gql`
  type Playback @cacheControl(scope: PRIVATE, maxAge: 0) {
    media: PlaybackMedia
    rule: PlaybackRule
    playback_session_id: String
    log_params: JSONObject
    error: PlaybackError
    mediaSource: JSONObject
    resumePoint: Int
    entitlements: [Entitlement]
  }

  type PlaybackMedia @cacheControl(scope: PRIVATE, maxAge: 0) {
    media_id: ID!
    uuid: String
    ovp_id: Int
    ovp_video_id: String
    thumbnail_tile_urls: [String]
  }

  type PlaybackRule {
    playback_rule_id: ID
    enable_seek_flag: Boolean
    enable_cromecst_flag: Boolean
    enable_airplay_flag: Boolean
    enable_mirroring_flag: Boolean
    enable_external_monitor_flag: Boolean
    enable_catchup_flag: Boolean
    enable_start_over_flag: Boolean
    enable_pause_flag: Boolean
    enable_background_play_flag: Boolean
    enable_screen_capture_flag: Boolean
    enable_playback_rate_change_flag: Boolean
    enable_offline_play_flag: Boolean
    maximum_bitrate: Int
    minimum_bitrate: Int
  }

  type PlaybackError {
    type: String
    message: String
    code: Int
  }

  type MediaSource @cacheControl(maxAge: 0) {
    media: JSONObject
    error: PlaybackError
  }

  type PlaybackSessionCheckResult {
    result: Boolean
  }

  type PlaybackSessionCloseResult {
    result: Boolean
  }

  type Query {
    playbackAuth(id: Int, vuid: String, sessionId: String, fl: Int): Playback
  }

  type Mutation {
    playbackSessionOpen(videoId: String, sessionId: String): MediaSource
    playbackSessionCheck(sessionId: String): PlaybackSessionCheckResult
    playbackSessionClose(sessionId: String): PlaybackSessionCloseResult
  }
`;

export const resolvers = {
  Query: {
    playbackAuth: async (
      _parent,
      { id, vuid, sessionId, fl },
      { dataSources }
    ) => {
      try {
        const playback = await dataSources.playbackAuth.auth({
          id,
          vuid,
          sessionId,
          fl,
          withResumePoint: true,
          exercise: true,
        });
        const { entitlements } = await dataSources.user.entitlementsFromMeta(
          id
        );
        return { ...playback, entitlements };
      } catch (e) {
        console.log("playback error", JSON.stringify(e.extensions, null, 2));
        // TODO: 同時視聴制限などエラーハンドリング
        if (e.extensions?.response?.body?.error?.code == 2050) {
          e.extensions.response.body.error.message =
            PLAYBACK_ERROR_MESSAGES[2050];
        }
        return e.extensions.response.body;
      }
    },
  },
  Mutation: {
    playbackSessionOpen: async (
      _parent,
      { videoId, sessionId },
      { dataSources }
    ) => {
      try {
        const mediaSource = await dataSources.playbackSession.open(
          videoId,
          sessionId
        );
        return { media: mediaSource };
      } catch (e) {
        console.log(
          "playback session error",
          JSON.stringify(e.extensions, null, 2)
        );
        const response = e.extensions.response;
        if (
          (response.status === 400 || response.status === 403) &&
          response?.body?.error
        ) {
          // エラーレスポンスに複数パターンがある?
          const errorCode = Number(response.body?.error);
          if (!isNaN(errorCode)) {
            return {
              error: {
                type: response.body?.type,
                message: response.body?.message,
                code: errorCode,
              },
            };
          } else if (
            response?.body?.result === false &&
            typeof response?.body?.error === "string"
          ) {
            const message =
              PLAYBACK_SESSION_ERROR_MESSAGES[
                PLAYBACK_SESSION_ERROR_CODES[response?.body?.error]
              ] || response?.body?.error;
            const code =
              PLAYBACK_SESSION_ERROR_CODES[response?.body?.error] ||
              PLAYBACK_SESSION_ERROR_CODES["other"];
            return {
              error: {
                type: response.body?.type,
                message,
                code,
              },
            };
          }
        } else {
          throw e;
        }
      }
    },
    playbackSessionCheck: async (_parent, { sessionId }, { dataSources }) => {
      const result = await dataSources.playbackSession.check(sessionId);
      return result;
    },
    playbackSessionClose: async (_parent, { sessionId }, { dataSources }) => {
      // TODO: 他のデバイスでクローズされたときにエラーになる？？
      try {
        const result = await dataSources.playbackSession.close(sessionId);
        return result;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
  },
  Playback: {
    rule: (playback) => playback.playback_rule,
    resumePoint: (playback) => playback.resume_point,
  },
};
